// Smart Daara - Physical school: teaching assignments (class × subject × teacher) + auto-assign
import React, { useEffect, useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { physicalAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Trash2, Wand2 } from 'lucide-react';
import { toast } from 'sonner';

export default function AssignmentsPage() {
  const { language } = useLanguage();
  const fr = language === 'fr';
  const [assignments, setAssignments] = useState([]);
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [form, setForm] = useState({ class_id: '', subject_id: '', teacher_user_id: '' });
  const [autoPreview, setAutoPreview] = useState(null);
  const [autoLoading, setAutoLoading] = useState(false);

  const load = async () => {
    const [a, c, s, t] = await Promise.all([
      physicalAPI.listAssignments(), physicalAPI.listClasses(), physicalAPI.listSubjects(), physicalAPI.listTeachers(),
    ]);
    setAssignments(a.data); setClasses(c.data); setSubjects(s.data); setTeachers(t.data);
  };
  useEffect(() => { load().catch(() => {}); }, []);

  const create = async () => {
    if (!form.class_id || !form.subject_id || !form.teacher_user_id) {
      toast.error(fr ? 'Classe, matière et enseignant requis' : 'Class, subject and teacher required'); return;
    }
    try {
      await physicalAPI.createAssignment(form);
      toast.success(fr ? 'Affectation enregistrée' : 'Assignment saved');
      setForm({ class_id: '', subject_id: '', teacher_user_id: '' });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const runAuto = async (apply) => {
    setAutoLoading(true);
    try {
      const res = await physicalAPI.autoAssign(apply);
      if (apply) {
        toast.success(fr ? `${res.data.proposals.length} affectation(s) appliquée(s)` : `${res.data.proposals.length} assignment(s) applied`);
        setAutoPreview(null);
        load();
      } else {
        setAutoPreview(res.data);
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setAutoLoading(false); }
  };

  return (
    <div className="space-y-6" data-testid="assignments-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-outfit font-bold text-slate-900">{fr ? 'Affectations enseignants' : 'Teaching assignments'}</h1>
        <Button variant="outline" onClick={() => runAuto(false)} disabled={autoLoading} data-testid="auto-assign-btn">
          <Wand2 className="w-4 h-4 mr-2" />{fr ? 'Affectation automatique' : 'Auto-assign'}
        </Button>
      </div>

      <Card className="border-0 shadow-sm">
        <CardHeader><CardTitle className="text-base">{fr ? 'Affecter manuellement' : 'Assign manually'}</CardTitle></CardHeader>
        <CardContent>
          <div className="grid sm:grid-cols-4 gap-3 items-end">
            <div>
              <Label className="text-xs">{fr ? 'Classe' : 'Class'}</Label>
              <select className="w-full h-10 border border-slate-200 rounded-md px-2 text-sm" value={form.class_id} onChange={(e) => setForm({ ...form, class_id: e.target.value })} data-testid="assign-class-select">
                <option value="">—</option>
                {classes.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs">{fr ? 'Matière' : 'Subject'}</Label>
              <select className="w-full h-10 border border-slate-200 rounded-md px-2 text-sm" value={form.subject_id} onChange={(e) => setForm({ ...form, subject_id: e.target.value })} data-testid="assign-subject-select">
                <option value="">—</option>
                {subjects.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs">{fr ? 'Enseignant' : 'Teacher'}</Label>
              <select className="w-full h-10 border border-slate-200 rounded-md px-2 text-sm" value={form.teacher_user_id} onChange={(e) => setForm({ ...form, teacher_user_id: e.target.value })} data-testid="assign-teacher-select">
                <option value="">—</option>
                {teachers.map((t) => <option key={t.user_id} value={t.user_id}>{t.first_name} {t.last_name}</option>)}
              </select>
            </div>
            <Button className="bg-emerald-700" onClick={create} data-testid="assign-create-btn">{fr ? 'Affecter' : 'Assign'}</Button>
          </div>
        </CardContent>
      </Card>

      <div className="space-y-2">
        {assignments.map((a) => (
          <div key={a.id} className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 bg-white rounded-lg shadow-sm">
            <div className="flex flex-wrap items-center gap-2">
              <Badge className="bg-slate-100 text-slate-700">{a.class_name}</Badge>
              <span className="font-medium">{a.subject_name}</span>
              <span className="text-slate-400">→</span>
              <span>{a.teacher_name}</span>
              <span className="text-xs text-slate-400">{a.weekly_hours}h/sem</span>
            </div>
            <button onClick={async () => { await physicalAPI.deleteAssignment(a.id); load(); }} className="text-red-400 hover:text-red-600" data-testid={`delete-assignment-${a.id}`}>
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
        {assignments.length === 0 && <p className="text-slate-400 text-sm text-center py-6">{fr ? 'Aucune affectation' : 'No assignments'}</p>}
      </div>

      <Dialog open={!!autoPreview} onOpenChange={() => setAutoPreview(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="auto-assign-preview">
          <DialogHeader><DialogTitle>{fr ? 'Proposition d\'affectation automatique' : 'Auto-assignment proposal'}</DialogTitle></DialogHeader>
          {autoPreview?.proposals?.length === 0 && <p className="text-slate-500">{fr ? 'Rien à affecter : tout est déjà couvert.' : 'Nothing to assign: everything covered.'}</p>}
          <div className="space-y-1">
            {autoPreview?.proposals?.map((p, i) => (
              <p key={i} className="text-sm"><b>{p.class_name}</b> · {p.subject_name} → {p.teacher_name} <span className="text-slate-400">({p.weekly_hours}h)</span></p>
            ))}
          </div>
          {autoPreview?.uncovered?.length > 0 && (
            <div className="bg-red-50 rounded-lg p-3 space-y-1">
              <p className="font-medium text-red-700 text-sm">{fr ? 'Non couverts :' : 'Uncovered:'}</p>
              {autoPreview.uncovered.map((u, i) => (
                <p key={i} className="text-xs text-red-600">{u.class_name} · {u.subject_name} — {u.reason}</p>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setAutoPreview(null)}>{fr ? 'Annuler' : 'Cancel'}</Button>
            <Button className="bg-emerald-700" disabled={!autoPreview?.proposals?.length || autoLoading} onClick={() => runAuto(true)} data-testid="auto-assign-apply-btn">
              {fr ? 'Appliquer' : 'Apply'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
